import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import Api from "../../../core/services/api/api";
import CacheService from "../../../core/services/cache/cache_service";
import LocalStorageService from "../../../core/services/cache/local_storage_service";
import {ConfigurationDataTypes, ConfigurationTypes} from "../../../core/constants/enums";
import {useAuthenticationState} from "../use-auth-state";
import useIsMounted from "../use-is-mounted";

/**
 * Known configuration property ids used by the application.
 * Prefer {@link PartnerConfigurationController.getValue} with a selector instead of importing these.
 */
export const ConfigurationsIds = {
    /**
     * Default notify / send email when creating or assigning an online user profile.
     */
    sendEmailUponCustomerRegistration: 10,
    /**
     * Default for “Send a copy to me as BCC” on invoice / order / quote emails.
     */
    sendBccOfInvoicesEmailToOperator: 11,
    /**
     * The margin percentage for sales orders preauth payments done via credit cards.
     */
    salesPreAuthMarginPercentage: 12,
    /**
     * When true, auto-generate customer/vendor codes; when false, require a code.
     */
    autoGenerateCustomerNumber: 13,
} as const;

export type ConfigurationIdKey = keyof typeof ConfigurationsIds;

export interface PartnerConfigurationDefaultValue {
    id: number;
    title: string;
}

export interface PartnerConfigurationProperty {
    id: number;
    name: string;
    description?: string;
    dynamicDataTypeId: number;
    dynamicDataTypeName?: string;
    typeId: number;
    typeTitle?: string;
    index: number;
    groupId: number;
    groupTitle?: string;
    isActive: boolean;
    defaultValues?: PartnerConfigurationDefaultValue[];
}

export interface PartnerConfigurationValueEntry {
    propertyId: number;
    partnerValue: string | null;
    operatorValue: string | null;
}

export interface PartnerConfigurationCachePayload {
    userId: string | number | null;
    fetchedAt: number;
    properties: PartnerConfigurationProperty[];
    values: PartnerConfigurationValueEntry[];
}

export interface FetchPartnerConfigurationsArgs {
    /**
     * When true (default), reuse in-memory / localStorage cache if TTL is valid for the current user.
     * When false, always fetch from the server.
     */
    useCached?: boolean;
}

export interface PartnerConfigurationController {
    fetchConfigurations: (args?: FetchPartnerConfigurationsArgs) => Promise<PartnerConfigurationCachePayload | null>;
    getValue: (selector: (ids: typeof ConfigurationsIds) => number) => unknown | null;
    getPartnerValue: (propertyId: number) => string | null;
    getOperatorValue: (propertyId: number) => string | null;
    setPartnerValue: (propertyId: number, value: string | null) => Promise<boolean>;
    setOperatorValue: (propertyId: number, value: string | null) => Promise<boolean>;
    properties: PartnerConfigurationProperty[];
    values: PartnerConfigurationValueEntry[];
    loading: boolean;
}

const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

const PartnerConfigurationStateContext = createContext<{
    properties: PartnerConfigurationProperty[];
    values: PartnerConfigurationValueEntry[];
    loading: boolean;
}>({
    properties: [],
    values: [],
    loading: false,
});

const PartnerConfigurationControllerContext = createContext<PartnerConfigurationController>({
    fetchConfigurations: async () => null,
    getValue: () => null,
    getPartnerValue: () => null,
    getOperatorValue: () => null,
    setPartnerValue: async () => false,
    setOperatorValue: async () => false,
    properties: [],
    values: [],
    loading: false,
});

/**
 * Returns whether a stored configuration cell has a meaningful value.
 */
const hasConfigurationValue = (value: unknown): boolean => {
    return value !== null && value !== undefined && value !== '';
};

/**
 * Parses a raw stored value for boolean configuration properties.
 */
const parseBooleanConfigurationValue = (value: unknown): boolean | null => {
    if (value === true || value === 'true' || value === '1') {
        return true;
    }
    if (value === false || value === 'false' || value === '0') {
        return false;
    }
    return null;
};

/**
 * Reads the partner-configuration cache from localStorage when present and well-formed.
 */
const readLocalConfigurationCache = (): PartnerConfigurationCachePayload | null => {
    const raw = LocalStorageService.get(LocalStorageService.keys.partnerConfiguration);
    if (!raw) {
        return null;
    }
    try {
        const parsed = JSON.parse(raw) as PartnerConfigurationCachePayload;
        if (!parsed || !Array.isArray(parsed.properties) || !Array.isArray(parsed.values) || typeof parsed.fetchedAt !== 'number') {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
};

/**
 * Persists the partner-configuration cache to localStorage.
 */
const writeLocalConfigurationCache = (payload: PartnerConfigurationCachePayload) => {
    LocalStorageService.set(LocalStorageService.keys.partnerConfiguration, JSON.stringify(payload));
};

/**
 * Clears the partner-configuration cache from localStorage.
 */
const clearLocalConfigurationCache = () => {
    LocalStorageService.remove(LocalStorageService.keys.partnerConfiguration);
};

/**
 * Whether a cache payload is still within the one-day TTL for the given user.
 */
const isCacheValidForUser = (payload: PartnerConfigurationCachePayload | null, userId: string | number | null): boolean => {
    if (!payload) {
        return false;
    }
    if (String(payload.userId ?? '') !== String(userId ?? '')) {
        return false;
    }
    return Date.now() - payload.fetchedAt < CACHE_TTL_MS;
};

/**
 * Provider for partner configuration catalog + values (memory + localStorage, 1-day TTL).
 */
export const PartnerConfigurationProvider: React.FC<React.PropsWithChildren> = ({children}) => {
    const {loggedIn} = useAuthenticationState();
    const isMounted = useIsMounted();
    const [properties, setProperties] = useState<PartnerConfigurationProperty[]>([]);
    const [values, setValues] = useState<PartnerConfigurationValueEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const propertiesRef = useRef(properties);
    const valuesRef = useRef(values);
    const inFlightRef = useRef<Promise<PartnerConfigurationCachePayload | null> | null>(null);
    const lastFetchedUserIdRef = useRef<string | null>(null);
    const isWritingCacheRef = useRef(false);

    propertiesRef.current = properties;
    valuesRef.current = values;

    /**
     * Applies a cache payload to React state.
     */
    const applyPayload = useCallback((payload: PartnerConfigurationCachePayload) => {
        propertiesRef.current = payload.properties;
        valuesRef.current = payload.values;
        setProperties(payload.properties);
        setValues(payload.values);
        lastFetchedUserIdRef.current = payload.userId == null ? null : String(payload.userId);
    }, []);

    /**
     * Writes payload to localStorage and memory (marks write so storage listener ignores echo).
     */
    const persistPayload = useCallback((payload: PartnerConfigurationCachePayload) => {
        isWritingCacheRef.current = true;
        try {
            writeLocalConfigurationCache(payload);
        } finally {
            isWritingCacheRef.current = false;
        }
        applyPayload(payload);
    }, [applyPayload]);

    /**
     * Resolves the effective raw value for a property (operator over partner).
     */
    const resolveRawValue = useCallback((propertyId: number): string | null => {
        const entry = valuesRef.current.find((e) => e.propertyId === propertyId);
        if (!entry) {
            return null;
        }
        if (hasConfigurationValue(entry.operatorValue)) {
            return entry.operatorValue;
        }
        if (hasConfigurationValue(entry.partnerValue)) {
            return entry.partnerValue;
        }
        return null;
    }, []);

    /**
     * Returns a typed application value for a configuration property id.
     */
    const getValueById = useCallback((propertyId: number): unknown | null => {
        const raw = resolveRawValue(propertyId);
        if (raw === null) {
            return null;
        }
        const property = propertiesRef.current.find((p) => p.id === propertyId);
        if (property?.dynamicDataTypeId === ConfigurationDataTypes.boolean) {
            return parseBooleanConfigurationValue(raw);
        }
        return raw;
    }, [resolveRawValue]);

    /**
     * Fetches List + GetValues (or returns cache). Soft when useCached; force otherwise.
     */
    const fetchConfigurations = useCallback(async ({useCached = true}: FetchPartnerConfigurationsArgs = {}) => {
        if (!CacheService.isLoggedIn()) {
            return null;
        }
        const userId = CacheService.getOperatorId() ?? null;

        if (useCached) {
            const local = readLocalConfigurationCache();
            if (isCacheValidForUser(local, userId)) {
                applyPayload(local!);
                return local;
            }
        }

        if (inFlightRef.current) {
            return inFlightRef.current;
        }

        const request = (async (): Promise<PartnerConfigurationCachePayload | null> => {
            setLoading(true);
            try {
                const [listResponse, valuesResponse] = await Promise.all([
                    Api.listPartnerConfigurationProperties(),
                    Api.getPartnerConfigurationValues(),
                ]);
                if (!isMounted()) {
                    return null;
                }
                if (!listResponse?.resultFlag || !valuesResponse?.resultFlag) {
                    return null;
                }
                const payload: PartnerConfigurationCachePayload = {
                    userId,
                    fetchedAt: Date.now(),
                    properties: (listResponse.data ?? []) as PartnerConfigurationProperty[],
                    values: (valuesResponse.data ?? []) as PartnerConfigurationValueEntry[],
                };
                persistPayload(payload);
                return payload;
            } finally {
                inFlightRef.current = null;
                if (isMounted()) {
                    setLoading(false);
                }
            }
        })();

        inFlightRef.current = request;
        return request;
    }, [applyPayload, isMounted, persistPayload]);

    /**
     * Selector-based value lookup for runtime consumers.
     */
    const getValue = useCallback((selector: (ids: typeof ConfigurationsIds) => number) => {
        const propertyId = selector(ConfigurationsIds);
        return getValueById(propertyId);
    }, [getValueById]);

    /**
     * Returns the partner-scoped raw value for a property.
     */
    const getPartnerValue = useCallback((propertyId: number) => {
        return valuesRef.current.find((e) => e.propertyId === propertyId)?.partnerValue ?? null;
    }, []);

    /**
     * Returns the operator-scoped raw value for a property.
     */
    const getOperatorValue = useCallback((propertyId: number) => {
        return valuesRef.current.find((e) => e.propertyId === propertyId)?.operatorValue ?? null;
    }, []);

    /**
     * Patches a single value entry in memory + localStorage after a successful Set*.
     */
    const patchValueEntry = useCallback((propertyId: number, patch: Partial<Pick<PartnerConfigurationValueEntry, 'partnerValue' | 'operatorValue'>>) => {
        const nextValues = [...valuesRef.current];
        const index = nextValues.findIndex((e) => e.propertyId === propertyId);
        if (index >= 0) {
            nextValues[index] = {...nextValues[index], ...patch};
        } else {
            nextValues.push({
                propertyId,
                partnerValue: patch.partnerValue ?? null,
                operatorValue: patch.operatorValue ?? null,
            });
        }
        const payload: PartnerConfigurationCachePayload = {
            userId: CacheService.getOperatorId() ?? null,
            fetchedAt: Date.now(),
            properties: propertiesRef.current,
            values: nextValues,
        };
        persistPayload(payload);
    }, [persistPayload]);

    /**
     * Sets a partner-level configuration value and updates the cache.
     */
    const setPartnerValue = useCallback(async (propertyId: number, value: string | null) => {
        const response = await Api.setPartnerConfigurationValue({propertyId, value});
        if (!isMounted()) {
            return false;
        }
        if (response?.resultFlag) {
            patchValueEntry(propertyId, {partnerValue: value});
            return true;
        }
        return false;
    }, [isMounted, patchValueEntry]);

    /**
     * Sets an operator-level configuration value and updates the cache.
     */
    const setOperatorValue = useCallback(async (propertyId: number, value: string | null) => {
        const response = await Api.setOperatorConfigurationValue({propertyId, value});
        if (!isMounted()) {
            return false;
        }
        if (response?.resultFlag) {
            patchValueEntry(propertyId, {operatorValue: value});
            return true;
        }
        return false;
    }, [isMounted, patchValueEntry]);

    /**
     * Soft-load on auth; force-fetch when the operator user id changes.
     */
    useEffect(() => {
        if (!loggedIn) {
            propertiesRef.current = [];
            valuesRef.current = [];
            setProperties([]);
            setValues([]);
            lastFetchedUserIdRef.current = null;
            return;
        }
        const userId = CacheService.getOperatorId();
        const userIdKey = userId == null ? null : String(userId);
        const userChanged = lastFetchedUserIdRef.current !== null && lastFetchedUserIdRef.current !== userIdKey;
        void fetchConfigurations({useCached: !userChanged});
    }, [fetchConfigurations, loggedIn]);

    /**
     * Force-fetch when operatorId in localStorage changes (login / user switch).
     */
    useEffect(() => {
        const onStorage = (e: StorageEvent) => {
            if (e.key === LocalStorageService.keys.partnerConfiguration) {
                if (isWritingCacheRef.current) {
                    return;
                }
                if (!e.newValue) {
                    propertiesRef.current = [];
                    valuesRef.current = [];
                    setProperties([]);
                    setValues([]);
                    return;
                }
                const local = readLocalConfigurationCache();
                if (local && isCacheValidForUser(local, CacheService.getOperatorId() ?? null)) {
                    applyPayload(local);
                }
                return;
            }
            if (e.key === LocalStorageService.keys.operatorId && loggedIn) {
                void fetchConfigurations({useCached: false});
            }
            if (e.key === LocalStorageService.keys.token && !CacheService.isLoggedIn()) {
                clearLocalConfigurationCache();
                propertiesRef.current = [];
                valuesRef.current = [];
                setProperties([]);
                setValues([]);
                lastFetchedUserIdRef.current = null;
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [applyPayload, fetchConfigurations, loggedIn]);

    const controller = useMemo<PartnerConfigurationController>(() => ({
        fetchConfigurations,
        getValue,
        getPartnerValue,
        getOperatorValue,
        setPartnerValue,
        setOperatorValue,
        properties,
        values,
        loading,
    }), [
        fetchConfigurations,
        getValue,
        getPartnerValue,
        getOperatorValue,
        setPartnerValue,
        setOperatorValue,
        properties,
        values,
        loading,
    ]);

    const stateValue = useMemo(() => ({properties, values, loading}), [properties, values, loading]);

    return (
        <PartnerConfigurationControllerContext.Provider value={controller}>
            <PartnerConfigurationStateContext.Provider value={stateValue}>
                {children}
            </PartnerConfigurationStateContext.Provider>
        </PartnerConfigurationControllerContext.Provider>
    );
};

/**
 * Hook for partner configuration controller (fetch / getValue / set*).
 */
export const usePartnerConfigurationController = (): PartnerConfigurationController => {
    return useContext(PartnerConfigurationControllerContext);
};

/**
 * Hook for partner configuration state (properties, values, loading).
 */
export const usePartnerConfigurationState = () => {
    return useContext(PartnerConfigurationStateContext);
};

/**
 * Convenience hook combining state + controller for settings UI and consumers.
 */
export const usePartnerConfiguration = () => {
    const state = usePartnerConfigurationState();
    const controller = usePartnerConfigurationController();
    return {
        ...controller,
        ...state,
        ConfigurationTypes,
        ConfigurationDataTypes,
        ConfigurationsIds,
    };
};

export default usePartnerConfiguration;
